import { Component, ChangeDetectionStrategy } from '@angular/core';
import { Form, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { FormComponentBase } from '../../../core/component/forms/form-component.base';
import { DataService } from '../../../core/service/base/data.service';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { DeleteFormCommandHandler } from '../../../core/service/command/delete-form-command-handler.service';
import { FormCommandHandlerResolver } from '../../../core/service/command/form-command-handler-resolver.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { ActivatedRoute } from '@angular/router';
import { PriceListItem } from '../../model/pricelistitem';
import { ObjectComponentCategory } from '../../../rent/model/object-component-category';
import { CustomProperty } from '../../../rent/model/custom-property';
import { SelectionItem } from '../../../core/component/select/select.component';
import { CustomPropertyService } from '../../../rent/service/data/custom-property.service';
import { ObjectComponentCategoryService } from '../../../rent/service/data/object-component-category.service';
import { AccountPlanService } from '../../../accounting/service/data/account-plan.service';
import { Account } from '../../../accounting/model/account';
import { AccountPlan } from '../../../accounting/model/account-plan';
import { ObjectComponentType } from '../../../rent/model/object-component-type';
import { ArticleUnit, ArticleUnitKindToString, CustomPropertyDataType } from '../../../rent/model/const/enums';
import { PriceListItemService } from '../../service/data/pricelist-item.service';
import { switchMap, tap } from 'rxjs/operators';
import { PriceListService } from '../../service/data/pricelist.service';
import { Money } from '../../../core/model/money';
import { CommandBase, standardCommand } from '../../../core/model/command';
import { PriceList, PriceListType } from '../../model/pricelist';
import { DimensionLabelsService } from '../../../accounting/service/data/dimension-labels.service';
import { LanguageService } from '../../../core/service/language.service';

@Component({
    selector: 'app-pricelist-item',
    templateUrl: './pricelist-item-form.component.html',
    providers: [
        FormCommandHandlerResolver,
        DeleteFormCommandHandler,
        PriceListItemService,
        PriceListService,
        { provide: DataService, useExisting: PriceListItemService },
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PriceListItemFormComponent extends FormComponentBase<PriceListItem> {
  constructor(
    private _fcf: FormControlExFactory,
    private _fb: FormBuilder,
    private _service: PriceListItemService,
    private _listService: PriceListService,
    private _objectComponentCategoryService: ObjectComponentCategoryService,
    private _customPropertyService: CustomPropertyService,
    private _accountPlanService: AccountPlanService,
    private readonly _dimensionLabelsService: DimensionLabelsService,
    private readonly _languageService: LanguageService,
    private activatedRoute: ActivatedRoute,
    commandDispatcher: CommandDispatcher,
    handlerResolver: FormCommandHandlerResolver,
    messageService: MessageService,
    navigation: NavigationService
  ) {
    super(activatedRoute, _service, messageService, navigation, commandDispatcher, handlerResolver);
  }

  protected createModel(): PriceListItem {
    return new PriceListItem();
  }

  componentCategorySelection: SelectionItem[] = [];
  componentTypeSelection: SelectionItem[] = [];
  customPropertySelection: SelectionItem[] = [];
  incomeAccountSelection: SelectionItem[] = [];
  accountPlans: AccountPlan[] = [];

  componentCategories: ObjectComponentCategory[];
  customProperties: CustomProperty[];
  incomeAccounts: { accountPlanId: string; accountPlanName: string; accounts: SelectionItem[] }[] = [];
  getIncomeAccountSelection(accountPlanId: string): SelectionItem[] {
    return this.incomeAccounts.find(a => a.accountPlanId === accountPlanId).accounts;
  }
  getAccountPlan(accountPlanId: string): AccountPlan | undefined {
    return this.accountPlans.find(plan => plan.id === accountPlanId);
  }

  showPriceListItemCostPlace(accountPlanId: string): boolean {
    const accountPlan = this.getAccountPlan(accountPlanId);
    return !!accountPlan?.enableCostPlace && !!accountPlan?.enableArticleCostPlace;
  }

  showPriceListItemFunction(accountPlanId: string): boolean {
    const accountPlan = this.getAccountPlan(accountPlanId);
    return !!accountPlan?.enableFunction;
  }

  codingForms: FormArray;

  vatPercentages = [
    { name: 'ARTICLE.NO_VAT', value: 0 },
    { name: '25%', value: 25 },
    { name: '12%', value: 12 },
    { name: '6%', value: 6 },
  ];

  get isInvoicing(): boolean {
    return this.priceList?.listType !== PriceListType.InternalReporting;
  }

  get showComponentType(): boolean {
    return this.componentTypeSelection.length > 0;
  }

  unitSelection = [
    { name: ArticleUnitKindToString(ArticleUnit.None), value: ArticleUnit.None },
    { name: ArticleUnitKindToString(ArticleUnit.Units), value: ArticleUnit.Units },
    { name: ArticleUnitKindToString(ArticleUnit.M2), value: ArticleUnit.M2 },
    { name: ArticleUnitKindToString(ArticleUnit.M3), value: ArticleUnit.M3 },
    { name: ArticleUnitKindToString(ArticleUnit.Meter), value: ArticleUnit.Meter },
    { name: ArticleUnitKindToString(ArticleUnit.Km), value: ArticleUnit.Km },
    { name: ArticleUnitKindToString(ArticleUnit.Gram), value: ArticleUnit.Gram },
    { name: ArticleUnitKindToString(ArticleUnit.Kg), value: ArticleUnit.Kg },
    { name: ArticleUnitKindToString(ArticleUnit.Ton), value: ArticleUnit.Ton },
    { name: ArticleUnitKindToString(ArticleUnit.Litre), value: ArticleUnit.Litre },
    { name: ArticleUnitKindToString(ArticleUnit.Hour), value: ArticleUnit.Hour },
  ];

  private priceList: PriceList;
  protected init(data: PriceListItem) {
    this.dataContext = data;
    this.dataContextInitialized$.next(true);

    this.AddSubscription(
      this.activatedRoute.paramMap
        .pipe(
          switchMap(map => {
            let listId = map.get('priceListId');
            return this._listService.getFilteredList({ id: listId });
          }),
          switchMap(list => {
            this.priceList = list[0];
            if (this.isNew) {
              let zero = new Money(this.priceList.currencyCode, 0);
              this.dataContext.amount = zero;
              this.dataContext.startCost = zero;
              this.dataContext.priceListId = this.priceList.id;
            }
            this.form = this.getFormGroup();
            return this._customPropertyService.getList();
          }),
          switchMap(result => {
            this.customProperties = result;
            return this._objectComponentCategoryService.getList();
          }),
          switchMap(result => {
            this.componentCategories = result;
            this.componentCategorySelection = result.map(c => new SelectionItem(c.name, c.id));
            this._categoryChanged(this.dataContext.componentCategory);
            this._setCustomPropertySelection(this.dataContext.componentType);
            return this._accountPlanService.getExtensiveList();
          })
        )
        .subscribe(result => {
          this.accountPlans = result;
          this.incomeAccounts = result.map(p => {
            return {
              accountPlanId: p.id,
              accounts: (this.priceList.listType === PriceListType.ExternalInvoicing ? p.incomeAccounts : p.accounts).map(
                a => new SelectionItem(a.toString(), a.id)
              ),
              accountPlanName: p.name,
            };
          });
          for (let accountPlan of this.incomeAccounts) {
            accountPlan.accounts.splice(0, 0, new SelectionItem('ACCOUNT_PLAN.STANDARD_ACCOUNT', null));
            var coding = this.dataContext.codings.find(c => c.accountPlanId === accountPlan.accountPlanId);
            const accountPlaceholder =
              this.priceList.listType === PriceListType.InternalReporting ? 'COMMON.COST_ACCOUNT' : 'COMMON.INCOME_ACCOUNT';
            const labels = this._dimensionLabelsService.fromAccountPlan(this.getAccountPlan(accountPlan.accountPlanId));
            this.codingForms.push(
              this._fb.group({
                id: this._fcf.createHidden(coding ? coding.id : null),
                accountPlanId: this._fcf.createHidden(accountPlan.accountPlanId),
                accountPlanName: this._fcf.createHidden(accountPlan.accountPlanName),
                incomeAccountId: this._fcf.create(accountPlaceholder, coding ? coding.incomeAccountId : null),
                costPlace: this._fcf.createWithOptions(labels.costPlaceLabel, coding ? coding.costPlace : null, {
                  length: 10,
                  helpText: this._languageService.getText('PRICELIST.HELP_TEXT.ACCOUNT_PLAN_COST_PLACE', {
                    dimension: labels.costPlaceLabel,
                  }),
                }),
                function: this._fcf.createWithOptions(labels.functionLabel, coding ? coding.function : null, {
                  length: 10,
                  helpText: this._languageService.getText('PRICELIST.HELP_TEXT.ACCOUNT_PLAN_FUNCTION', {
                    dimension: labels.functionLabel,
                  }),
                }),
              })
            );
          }
        })
    );
  }

  protected getFormGroup(): FormGroup {
    const item = this.dataContext;
    this.codingForms = this._fb.array([]);
    const form = this._fb.group({
      name: this._fcf.createMandatory('PRICELIST.PRICELIST_ITEM_NAME', item.name),
      id: this._fcf.createHidden(item.id),
      priceListId: this._fcf.createHidden(item.priceListId),
      amount: this._fcf.createMandatory(
        'PRICELIST.PRICE_PER_UNIT',
        item.amount && item.amount.currencyCode ? item.amount : new Money(item.currencyCode, 0)
      ),
      unit: this._fcf.createMandatory('ARTICLE.UNIT', item.unit),
      componentCategory: this._fcf.create('COMPONENT.COMPONENT_CATEGORY', item.componentCategory),
      componentType: this._fcf.create('COMPONENT.TYPE', item.componentType),
      customProperty: this._fcf.create('PRICELIST.CALCULATE_BY_CUSTOM_PROPERTY', item.customProperty),
      vatPercent: this._fcf.create('ARTICLE.VAT_PERCENT', item.vatPercent),
      startCost: this._fcf.createMandatory(
        'PRICELIST.START_COST',
        item.startCost && item.startCost.currencyCode ? item.startCost : new Money(item.currencyCode, 0)
      ),
      code: this._fcf.create('PRICELIST.CODE', item.code, Validators.maxLength(15)),
      codings: this.codingForms,
    });

    this.AddSubscription(form.controls['componentCategory'].valueChanges.subscribe(value => this._categoryChanged(value)));

    this.AddSubscription(form.controls['componentType'].valueChanges.subscribe(value => this._typeChanged(value)));

    return form;
  }

  private _typeChanged(componentTypeId: string) {
    this.dataContext.componentType = componentTypeId;
    this._setCustomPropertySelection(componentTypeId);
    const propertyControl = this.form.controls['customProperty'];
    if (propertyControl.value && this.customPropertySelection.find(ps => ps.value === propertyControl.value) === undefined)
      propertyControl.setValue(null);
  }

  private _categoryChanged(componentCategoryId: string) {
    this.dataContext.componentCategory = componentCategoryId;
    this._setComponentTypeSelection(componentCategoryId);
    let cat = this.componentCategories.find(cat => cat.id === componentCategoryId);
    let currentType = this.form.controls['componentType'].value;
    if (this._getComponentTypes(componentCategoryId).find(type => type.id === currentType) === undefined) {
      this.form.controls['componentType'].setValue(null);
    }
    this.form.controls['componentType'].setValidators(cat === undefined ? [] : [Validators.required]);
  }

  private _setComponentTypeSelection(componentCategoryId: string) {
    this.componentTypeSelection = this._getComponentTypes(componentCategoryId)
      .map(type => new SelectionItem(type.name, type.id))
      .sort((a, b) => (a.name.toUpperCase() < b.name.toUpperCase() ? -1 : a.name.toUpperCase() > b.name.toUpperCase() ? 1 : 0));
  }

  private _setCustomPropertySelection(componentTypeId: string) {
    let category = this.dataContext.componentCategory;
    let type = this._getComponentTypes(category).find(t => t.id === componentTypeId);

    this.customPropertySelection = (type === undefined ? this.customProperties : type.customProperties.map(p => p.property))
      .filter(prp => prp.dataType === CustomPropertyDataType.Numeric)
      .map(prp => new SelectionItem(prp.caption, prp.id))
      .sort((a, b) => (a.name.toUpperCase() < b.name.toUpperCase() ? -1 : a.name.toUpperCase() > b.name.toUpperCase() ? 1 : 0));
  }

  private _getComponentTypes(categoryId: string): ObjectComponentType[] {
    if (!categoryId) return [];

    let category = this.componentCategories.find(cat => cat.id === categoryId);
    return category === undefined ? [] : category.objectComponentTypes;
  }

  private navigateToList() {
    this.navigation.navigate(['pricelists', 'edit', this.form.controls['priceListId'].value]);
  }

  navigateAfterSave(id: any) {
    this.navigateToList();
  }

  protected executedWithResult(result: boolean, command: CommandBase): void {
    if (result && command.id === standardCommand.delete) {
      this.navigateToList();
    }
  }

  cancel() {
    this.navigateToList();
  }
}
